import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StepperSelectionEvent } from '@angular/cdk/stepper';

import { PolicyTemplateService, policyNameUniqueValidator } from '../../../../services/entity-services/policy-template.service';
import { PolicyTemplateSummary } from '../../../../interfaces/policy-templates/policy-template-summary.interface';
import { CreatePolicyTemplateRequest } from '../../../../interfaces/policy-templates/create-policy-template-request.interface';
import { DuplicateCheckRequest } from '../../../../interfaces/policy-templates/duplicate-check.interface';
import {
  VmCleanupAction,
  VmPolicySteps,
  GracePeriod,
} from '../../../../interfaces/policy-templates/policy-cleanup-actions.interface';

@Component({
  selector: 'app-vm-policy-wizard',
  templateUrl: './vm-policy-wizard.component.html',
  styleUrls: ['./vm-policy-wizard.component.css']
})
export class VmPolicyWizardComponent implements OnInit {
  infoForm!: FormGroup;
  creationForm!: FormGroup;
  dailyCleanupForm!: FormGroup;
  classEndCleanupForm!: FormGroup;

  isDuplicateChecking = false;
  duplicatePolicy: PolicyTemplateSummary | null = null;
  isSubmitting = false;
  drawerPolicy: PolicyTemplateSummary | null = null;

  readonly vmCreationDescription = 'VMs are created when a participant accesses the class for the first time.';

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly policyTemplateService: PolicyTemplateService,
  ) {}

  ngOnInit(): void {
    this.infoForm = this.fb.group({
      policyName: ['', {
        validators: [
          Validators.required,
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/)
        ],
        asyncValidators: [policyNameUniqueValidator(this.policyTemplateService, 'Vm')],
        updateOn: 'change'
      }],
      description: ['', [Validators.maxLength(500)]]
    });

    this.creationForm = this.fb.group({
      creationMode: ['on_demand', Validators.required]
    });

    // Daily: grace period is minutes-only (5–360), default 60 min
    this.dailyCleanupForm = this.fb.group({
      actionType: ['DoNothing', Validators.required],
      gracePeriodValue: [60],
      gracePeriodUnit: ['Minutes'],
      destroyScope: [null]
    }, { validators: this.dailyCleanupGroupValidator() });

    // Class-end: grace period is hours/days only, default 1 hour
    this.classEndCleanupForm = this.fb.group({
      actionType: ['DoNothing', Validators.required],
      gracePeriodValue: [1],
      gracePeriodUnit: ['Hours'],
      destroyScope: [null]
    }, { validators: this.classEndCleanupGroupValidator() });
  }

  /** Daily cleanup: grace period locked to minutes, range 5–360. */
  private dailyCleanupGroupValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const action = group.get('actionType')?.value;
      if (action === 'DoNothing') return null;
      const errors: ValidationErrors = {};
      const gpv = group.get('gracePeriodValue')?.value;
      if (!gpv || gpv < 5 || gpv > 360) errors['gracePeriodValueRequired'] = true;
      if (action === 'Destroy') {
        const scope = group.get('destroyScope')?.value;
        if (!scope) errors['destroyScopeRequired'] = true;
      }
      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  /** Class-end cleanup: grace period in hours or days only (no minutes). */
  private classEndCleanupGroupValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const action = group.get('actionType')?.value;
      if (action === 'DoNothing') return null;
      const errors: ValidationErrors = {};
      const gpv = group.get('gracePeriodValue')?.value;
      const gpu = group.get('gracePeriodUnit')?.value;
      if (!gpv || gpv < 1) errors['gracePeriodValueRequired'] = true;
      if (!gpu || !['Hours', 'Days'].includes(gpu)) errors['gracePeriodUnitRequired'] = true;
      if (action === 'Destroy') {
        const scope = group.get('destroyScope')?.value;
        if (!scope) errors['destroyScopeRequired'] = true;
      }
      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  onStepChange(event: StepperSelectionEvent): void {
    if (event.selectedIndex === 4) {
      this.checkForDuplicate();
    }
  }

  checkForDuplicate(): void {
    this.isDuplicateChecking = true;
    this.duplicatePolicy = null;
    const request: DuplicateCheckRequest = {
      resourceType: 'Vm',
      vmSteps: this.buildVmSteps()
    };
    this.policyTemplateService.checkDuplicate(request).subscribe({
      next: (res) => {
        this.isDuplicateChecking = false;
        this.duplicatePolicy = res.isDuplicate ? res.existing : null;
      },
      error: () => { this.isDuplicateChecking = false; }
    });
  }

  private buildVmSteps(): VmPolicySteps {
    return {
      creationMode: 'on_demand',
      dailyCleanup: this.buildCleanupAction(this.dailyCleanupForm, 'daily'),
      classEndCleanup: this.buildCleanupAction(this.classEndCleanupForm, 'classEnd')
    };
  }

  private buildCleanupAction(form: FormGroup, constraint: 'daily' | 'classEnd'): VmCleanupAction {
    const type = form.get('actionType')!.value as string;
    if (type === 'DoNothing') return { actionType: 'DoNothing' };
    const gracePeriod: GracePeriod = {
      value: form.get('gracePeriodValue')!.value,
      unit: constraint === 'daily' ? 'Minutes' : form.get('gracePeriodUnit')!.value
    };
    if (type === 'Shutdown') return { actionType: 'Shutdown', gracePeriod };
    if (type === 'Deallocate') return { actionType: 'Deallocate', gracePeriod };
    return { actionType: 'Destroy', scope: form.get('destroyScope')!.value, gracePeriod };
  }

  formatCleanupAction(form: FormGroup, constraint: 'daily' | 'classEnd' = 'classEnd'): string {
    const action = form.get('actionType')?.value;
    if (action === 'DoNothing') return 'Do Nothing';
    const gpv = form.get('gracePeriodValue')?.value;
    const gpu = constraint === 'daily' ? 'Minutes' : form.get('gracePeriodUnit')?.value;
    const scope = form.get('destroyScope')?.value;
    const gpStr = `${gpv} ${gpu}`;
    const labels: Record<string, string> = {
      Shutdown: `Shutdown (grace: ${gpStr})`,
      Deallocate: `Deallocate (grace: ${gpStr})`,
      Destroy: `Destroy — ${scope === 'ComputeOnly' ? 'compute only' : 'compute + disk'} (grace: ${gpStr})`
    };
    return labels[action] ?? action;
  }

  get reviewPolicyName(): string { return this.infoForm?.get('policyName')?.value?.trim() ?? ''; }
  get reviewDescription(): string { return this.infoForm?.get('description')?.value?.trim() ?? ''; }

  openExistingPolicy(policy: PolicyTemplateSummary): void {
    this.drawerPolicy = policy;
  }

  submit(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    const request: CreatePolicyTemplateRequest = {
      resourceType: 'Vm',
      policyName: this.reviewPolicyName,
      description: this.reviewDescription,
      vmSteps: this.buildVmSteps()
    };
    this.policyTemplateService.create(request).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.router.navigate(['/policy-template']);
      },
      error: (err) => {
        this.isSubmitting = false;
        const msg = err?.error?.message ?? 'Failed to save policy template.';
        this.snackBar.open(msg, 'Dismiss', { duration: 5000 });
      }
    });
  }
}
